/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { ChangeEvent, useEffect, useRef, useState } from 'react'

import { Alert, Fade, Icon, Snackbar } from '@mui/material'
import Card from '@mui/material/Card'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { saveAs } from 'file-saver'
import Papa from 'papaparse'
import { useNavigate } from 'react-router-dom'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { updateRoutes, useMaterialUIController } from 'context'
import ManageSpecialLocationsForm from 'layouts/special-locations/manage-special-locations'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import DataTable from 'modules/Tables/DataTable'
import { ROUTE_PATHS, RouteType } from 'routes'
import { TabularData } from 'types/server/tabular'

import { SpecialLocationsForm } from '../types'
import { postNewSpecialLocations } from '../utils'

function SpecialLocations(): JSX.Element {
	const [controller, dispatch] = useMaterialUIController()
	const { session, routes } = controller

	const [uploadStatus, setUploadStatus] = useState<{
		status: 'loading' | 'error' | 'success' | 'warning' | null
		message?: string
	}>(null)
	const navigate = useNavigate()
	const fileInputRef = useRef<HTMLInputElement | null>(null)

	const fetchData = async () => {
		const response = await axios.get<TabularData>(
			`${process.env.REACT_APP_API_URL}/special-locations`,
			{
				headers: {
					Authorization: await session.user.getIdToken(),
				},
			}
		)
		return response.data
	}

	const { data, error, isLoading } = useQuery({
		queryKey: ['special-locations'],
		queryFn: fetchData,
	})

	const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (fileInputRef.current) fileInputRef.current.value = ''
		if (!file) return

		if (file.type !== 'text/csv') {
			console.log('Unsupported file type:', file.type)
			setUploadStatus({ status: 'error', message: 'Only CSV files are supported.' })
			return
		}

		setUploadStatus({ status: 'loading' })

		const reader = new FileReader()
		reader.onload = async () => {
			const text = reader.result as string
			// CSV content validation
			const lines = text.trim().split('\n')
			const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
			const requiredHeaders = ['names', 'lat', 'lng']

			// Check for required headers
			const hasAllHeaders = requiredHeaders.every(header => headers.includes(header))
			if (!hasAllHeaders) {
				setUploadStatus({
					status: 'error',
					message: 'CSV must contain the following headers: names, lat, lng.',
				})
				return
			}

			Papa.parse(text, {
				header: true,
				skipEmptyLines: true,
				complete: async function (results) {
					const rows = results.data
					const specialLocations: SpecialLocationsForm[] = []
					for (let i = 0; i < rows.length; i++) {
						const row = rows[i] as { names: string; lat: string; lng: string }

						if (!row['names']) {
							setUploadStatus({
								status: 'error',
								message: `Names not valid for row ${i + 2}. Must be present.`,
							})
							return
						}

						if (!row['lat'] || isNaN(Number(row['lat']))) {
							setUploadStatus({
								status: 'error',
								message: `Invalid lat in row ${i + 2}. Must be a valid number.`,
							})
							return
						}
						if (!row['lng'] || isNaN(Number(row['lng']))) {
							setUploadStatus({
								status: 'error',
								message: `Invalid lng in row ${i + 2}. Must be a valid number.`,
							})
							return
						}
						specialLocations.push({
							names: row.names.split(';').map(name => name.trim().replace(/^"|"$/g, '')),
							geometry: { lat: Number(row.lat), lng: Number(row.lng) },
						})
					}
					try {
						const success = await postNewSpecialLocations(session.user, specialLocations)
						if (success) {
							setUploadStatus({
								status: 'success',
								message: `Successfully uploaded ${specialLocations.length} special locations.`,
							})
						}
					} catch (err: any) {
						if (err?.response?.status === 400) {
							setUploadStatus({
								status: 'warning',
								message:
									'Partial success: 1 or more items contained duplicate names. These were not included.',
							})
						} else {
							setUploadStatus({
								status: 'error',
								message: 'An error occurred during upload.',
							})
						}
					}
				},
			})
		}
		reader.readAsText(file)
	}

	function downloadCSV(filename = 'special-locations.csv') {
		// Hidden fields in table request
		const columns = ['names', 'lat', 'lng']

		// map the data to only these fields
		const filtered = data.table.rows.map(row => {
			const out: any = {}
			columns.forEach(col => {
				out[col] = row[col]
			})
			return out
		})
		const csv = Papa.unparse(filtered)
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		saveAs(blob, filename)
	}

	useEffect(() => {
		if (data) {
			const allRows = data.table.rows
			const specialLocationRows: RouteType[] = allRows.map(e => ({
				type: 'invisible' as const,
				name: `Special Location: ${e.name}`,
				key: `special-location-${e.route as string}`,
				route: `${ROUTE_PATHS.SPECIAL_LOCATIONS.MANAGE}/${e.route as string}`,
				component: <ManageSpecialLocationsForm />,
				authRequired: true,
			}))
			updateRoutes(dispatch, routes, specialLocationRows)
		}
	}, [data])

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Snackbar
				open={['success', 'error', 'warning'].includes(uploadStatus?.status)}
				autoHideDuration={5000}
				onClose={() => {}}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				TransitionComponent={Fade}
			>
				{uploadStatus?.status !== 'loading' && (
					<Alert severity={uploadStatus?.status} variant="standard">
						{uploadStatus?.message}
					</Alert>
				)}
			</Snackbar>
			<MDBox pt={6} pb={3}>
				<Card>
					<MDBox p={3} mb={1} lineHeight={1}>
						<MDTypography variant="h2" fontWeight="medium">
							Special Locations
						</MDTypography>
						<MDTypography variant="button" color="text">
							{/* TODO: Add description for import and allow template CSV to allow self serve */}
							Manage locations that need more precise naming or coordinates. Ideal for large or
							complex places with multiple drop-off or pick-up points, such as hospitals or airport
							terminals.
						</MDTypography>
					</MDBox>
					<MDBox mx={3} display={'flex'} justifyContent={'space-between'} mb={2}>
						<MDBox display={'flex'} gap={3}>
							<MDButton
								color="primary"
								variant="outlined"
								onClick={() => navigate(ROUTE_PATHS.SPECIAL_LOCATIONS.NEW)}
							>
								ADD NEW
								<Icon sx={{ ml: 1 }}>add_circle_outlined</Icon>
							</MDButton>
						</MDBox>
						<MDBox display={'flex'} justifyContent={'flex-end'} gap={3}>
							<MDInput
								ref={fileInputRef}
								accept=".csv"
								type="file"
								id="csv-special-locations-import"
								style={{ display: 'none' }}
								onChange={(e: ChangeEvent<HTMLInputElement>) => handleFileUpload(e)}
							/>
							<label htmlFor="csv-special-locations-import">
								<MDButton component="span" color="primary" variant="outlined">
									IMPORT
									<Icon sx={{ ml: 1 }}>upload</Icon>
								</MDButton>
							</label>
							<MDButton color="primary" variant="outlined" onClick={() => downloadCSV()}>
								EXPORT
								<Icon sx={{ ml: 1 }}>download</Icon>
							</MDButton>
						</MDBox>
					</MDBox>
					{!isLoading && !error ? (
						<DataTable
							table={data.table}
							canSearch
							parentPath={ROUTE_PATHS.SPECIAL_LOCATIONS.MANAGE}
						/>
					) : (
						<LoadingComponent type="box" height={600} containerStyle={{ padding: 50 }} />
					)}
				</Card>
			</MDBox>
		</DashboardLayout>
	)
}

export default SpecialLocations
